import { createSlice } from '@reduxjs/toolkit';

import {
  getCreationSettingsApi,
  updateCreationSettingsApi,
  getCreationProcessStatusApi,
  getCreationMappingInfoApi,
  getCreationScopeCountApi,
  uploadCreationMappingApi,
  startCreationRunApi,
  stopCreationRunApi,
  downloadCreationRunReportCsvApi,
} from './listing-creation.api';

const initialState = {
  settings: null,
  getSettingsStatus: 'idle',
  updateSettingsStatus: 'idle',

  mappingInfo: null,
  getMappingInfoStatus: 'idle',
  uploadMappingStatus: 'idle',

  scopeCount: null,
  scopeCountDetail: null,
  getScopeCountStatus: 'idle',

  processStatus: null,
  getProcessStatusStatus: 'idle',
  isCreationRunning: false,

  startRunStatus: 'idle',
  stopRunStatus: 'idle',
  downloadRunReportStatus: 'idle',

  error: null,
};

const listingCreationSlice = createSlice({
  name: 'listingCreation',
  initialState,
  reducers: {
    setProcessStatusFromSocket(state, action) {
      state.processStatus = action.payload;
      state.isCreationRunning = Boolean(action.payload?.isRunning);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCreationSettingsApi.pending, (state) => {
        state.getSettingsStatus = 'loading';
      })
      .addCase(getCreationSettingsApi.fulfilled, (state, action) => {
        state.getSettingsStatus = 'succeeded';
        state.settings = action.payload;
      })
      .addCase(getCreationSettingsApi.rejected, (state, action) => {
        state.getSettingsStatus = 'failed';
        state.error = action.payload;
      })
      .addCase(updateCreationSettingsApi.pending, (state) => {
        state.updateSettingsStatus = 'loading';
      })
      .addCase(updateCreationSettingsApi.fulfilled, (state, action) => {
        state.updateSettingsStatus = 'succeeded';
        state.settings = { ...state.settings, ...action.payload };
      })
      .addCase(updateCreationSettingsApi.rejected, (state) => {
        state.updateSettingsStatus = 'failed';
      })
      .addCase(getCreationMappingInfoApi.fulfilled, (state, action) => {
        state.getMappingInfoStatus = 'succeeded';
        state.mappingInfo = action.payload;
      })
      .addCase(getCreationScopeCountApi.pending, (state) => {
        state.getScopeCountStatus = 'loading';
      })
      .addCase(getCreationScopeCountApi.fulfilled, (state, action) => {
        state.getScopeCountStatus = 'succeeded';
        state.scopeCount = action.payload?.count ?? 0;
        state.scopeCountDetail = action.payload ?? null;
      })
      .addCase(getCreationScopeCountApi.rejected, (state) => {
        state.getScopeCountStatus = 'failed';
        state.scopeCount = null;
        state.scopeCountDetail = null;
      })
      .addCase(uploadCreationMappingApi.fulfilled, (state, action) => {
        state.uploadMappingStatus = 'succeeded';
        state.mappingInfo = {
          exists: true,
          entryCount: action.payload?.entryCount ?? 0,
        };
      })
      .addCase(getCreationProcessStatusApi.fulfilled, (state, action) => {
        state.getProcessStatusStatus = 'succeeded';
        state.processStatus = action.payload;
        state.isCreationRunning = Boolean(action.payload?.isRunning);
      })
      .addCase(startCreationRunApi.fulfilled, (state, action) => {
        state.startRunStatus = 'succeeded';
        if (action.payload?.processStatus) {
          state.processStatus = action.payload.processStatus;
          state.isCreationRunning = Boolean(action.payload.processStatus.isRunning);
        } else {
          state.isCreationRunning = true;
        }
      })
      .addCase(startCreationRunApi.rejected, (state) => {
        state.startRunStatus = 'failed';
        state.isCreationRunning = false;
      })
      .addCase(stopCreationRunApi.fulfilled, (state) => {
        state.stopRunStatus = 'succeeded';
      })
      .addCase(downloadCreationRunReportCsvApi.pending, (state) => {
        state.downloadRunReportStatus = 'loading';
      })
      .addCase(downloadCreationRunReportCsvApi.fulfilled, (state) => {
        state.downloadRunReportStatus = 'succeeded';
      })
      .addCase(downloadCreationRunReportCsvApi.rejected, (state) => {
        state.downloadRunReportStatus = 'failed';
      });
  },
});

export const { setProcessStatusFromSocket } = listingCreationSlice.actions;
export default listingCreationSlice.reducer;
